import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import type { Tables } from '@/types/database';

/**
 * "My settings" data layer. Backs the per-user Settings page
 * (`/account/settings`). Only ever reads / writes the calling user's
 * OWN profile row — admin-side edits to other people's roles + active
 * flag still go through `src/features/users/hooks.ts`.
 *
 * Avatars are stored in the public `avatars` Supabase Storage bucket
 * defined in migration 46. Object keys are always
 *   `<organization_id>/<user_id>/<filename>`
 * so the bucket's storage RLS policies can decode the path and confirm
 * the caller is the avatar's owner. We persist BOTH the public URL
 * (`profiles.avatar_url`, what the topbar renders) AND the storage
 * key (`profiles.avatar_path`, what we hand back to `storage.remove()`
 * when the user replaces or removes the photo).
 */

const AVATARS_BUCKET = 'avatars';

const SELF_PROFILE_KEY = (userId: string | undefined) => ['my-profile', userId];

export type ProfileRow = Tables<'profiles'>;

/**
 * Fetch the calling user's full profile row. Returns ALL columns —
 * the Settings page needs the optional fields (phone, address, etc.)
 * that AuthProvider deliberately doesn't pull on every page load.
 */
export function useMyProfile() {
  const { user } = useAuth();
  return useQuery({
    queryKey: SELF_PROFILE_KEY(user?.id),
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user!.id)
        .single();
      if (error) throw error;
      return data as ProfileRow;
    },
  });
}

/**
 * Patch one or more fields on the caller's own profile row. The
 * `profile_update_self_display` policy in migration 02 already pins
 * the row to `auth.uid()` and forbids elevating role / is_active, so
 * we just hand the supabase-js client the partial update and let the
 * database enforce the column-level invariants.
 */
export function useUpdateMyProfile() {
  const qc = useQueryClient();
  const { user, refreshProfile } = useAuth();
  return useMutation({
    mutationFn: async (patch: Partial<ProfileRow>) => {
      if (!user?.id) throw new Error('Not signed in.');
      const { data, error } = await supabase
        .from('profiles')
        .update(patch)
        .eq('id', user.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as ProfileRow;
    },
    onSuccess: async (data) => {
      qc.setQueryData(SELF_PROFILE_KEY(user?.id), data);
      qc.invalidateQueries({ queryKey: ['profiles'] });
      // Refresh the AuthProvider snapshot so the topbar (and anything
      // else that reads from `useAuth().profile`) picks up the new
      // name / avatar without a page reload.
      await refreshProfile();
    },
  });
}

function sanitizeAvatarFilename(name: string): string {
  // Timestamp prefix so re-uploads land at unique keys and we can
  // delete the prior one; otherwise a same-name re-upload would
  // collide on `upsert: false`.
  const stamp = Date.now();
  const cleaned = name.replace(/[^a-zA-Z0-9._-]+/g, '_');
  return `${stamp}-${cleaned}`;
}

export function buildAvatarPath(args: {
  organization_id: string;
  user_id: string;
  filename: string;
}): string {
  return `${args.organization_id}/${args.user_id}/${sanitizeAvatarFilename(args.filename)}`;
}

/**
 * Upload (or replace) the caller's avatar. We:
 *   1. Push the new bytes to the `avatars` bucket
 *   2. Look up the resulting public URL (the bucket is public, so
 *      `getPublicUrl` is a pure path-join — no network round-trip)
 *   3. Patch `profiles.avatar_url` + `profiles.avatar_path` in one
 *      update so the topbar repaints with the new face
 *   4. Best-effort delete the prior object so the bucket doesn't
 *      accumulate orphans. Tolerated if it fails (e.g. RLS rejects a
 *      stale path that's already gone) — the visible result is still
 *      correct.
 */
export function useUploadAvatar() {
  const qc = useQueryClient();
  const { user, profile, refreshProfile } = useAuth();
  return useMutation({
    mutationFn: async (input: { file: File; previousPath: string | null }) => {
      if (!user?.id || !profile?.organization_id) {
        throw new Error('Not signed in.');
      }
      const path = buildAvatarPath({
        organization_id: profile.organization_id,
        user_id: user.id,
        filename: input.file.name,
      });

      const { error: uploadErr } = await supabase.storage
        .from(AVATARS_BUCKET)
        .upload(path, input.file, {
          upsert: true,
          cacheControl: '3600',
          contentType: input.file.type || undefined,
        });
      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage
        .from(AVATARS_BUCKET)
        .getPublicUrl(path);
      const publicUrl = urlData?.publicUrl ?? null;
      if (!publicUrl) throw new Error('Could not resolve avatar URL.');

      // Best-effort cleanup of the old object. We swallow errors so a
      // stale-path 404 / RLS rejection doesn't roll back a successful
      // upload from the user's perspective.
      if (input.previousPath && input.previousPath !== path) {
        await supabase.storage.from(AVATARS_BUCKET).remove([input.previousPath]);
      }

      const { data, error: dbErr } = await supabase
        .from('profiles')
        .update({ avatar_path: path, avatar_url: publicUrl })
        .eq('id', user.id)
        .select('*')
        .single();
      if (dbErr) throw dbErr;
      return data as ProfileRow;
    },
    onSuccess: async (data) => {
      qc.setQueryData(SELF_PROFILE_KEY(user?.id), data);
      qc.invalidateQueries({ queryKey: ['profiles'] });
      await refreshProfile();
    },
  });
}

/**
 * Remove the caller's user-uploaded avatar. Falls back to "no avatar"
 * even if the user originally signed in with a Google avatar — the
 * Google URL is gone from `profiles.avatar_url` once we've cleared it,
 * so the topbar uses the fallback initials. They can re-add later by
 * uploading a new file or signing out / back in (which the OAuth
 * bootstrap trigger would re-populate, though that path only fires on
 * NEW auth.users rows so in practice this is a one-way clear).
 */
export function useRemoveAvatar() {
  const qc = useQueryClient();
  const { user, refreshProfile } = useAuth();
  return useMutation({
    mutationFn: async (input: { path: string | null }) => {
      if (!user?.id) throw new Error('Not signed in.');
      if (input.path) {
        await supabase.storage.from(AVATARS_BUCKET).remove([input.path]);
      }
      const { data, error } = await supabase
        .from('profiles')
        .update({ avatar_path: null, avatar_url: null })
        .eq('id', user.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as ProfileRow;
    },
    onSuccess: async (data) => {
      qc.setQueryData(SELF_PROFILE_KEY(user?.id), data);
      qc.invalidateQueries({ queryKey: ['profiles'] });
      await refreshProfile();
    },
  });
}
